import type { IncidentTimeRange } from '../../pages/incidents/useIncidentsTimeRange';
import type { AfterSignedScanReviewContext, AfterSignedScanTimelineMode } from './afterSignedScanReview';
import { formatAfterSignedScanTimestamp } from './afterSignedScanReview';
import { formatObservationCount } from '../copy/countNoun';
import type { CustomerMetric } from '../customerMetrics';
import { metricUsesSignedScanWindow } from '../customerMetrics';

export type ReviewWindowScopeKind =
  | 'selected_review_window'
  | 'after_signed_scan'
  | 'full_case_history'
  | 'signed_scan_scope';

export type ReviewWindowContext = {
  kind: ReviewWindowScopeKind;
  timelineMode?: AfterSignedScanTimelineMode;
  fromIso: string | null;
  toIso: string | null;
  signedScanAt?: string | null;
  labels: {
    presetLabel?: string;
  };
};

export type CaseHistoryTimeBounds = {
  fromIso?: string | null;
  toIso?: string | null;
};

export type ResolveReviewWindowContextArgs = {
  timeRange: IncidentTimeRange;
  afterSignedScanReview?: AfterSignedScanReviewContext | null;
  fullHistoryMode?: boolean;
  caseHistoryBounds?: CaseHistoryTimeBounds | null;
  signedScanScope?: {
    start?: string | null;
    end?: string | null;
  } | null;
};

type CaseHistorySource = {
  first_seen?: string | null;
  started_at?: string | null;
  last_seen?: string | null;
  last_seen_at?: string | null;
};

function pickFirstInstant(...candidates: Array<string | null | undefined>): string | null {
  for (const candidate of candidates) {
    const text = String(candidate || '').trim();
    if (text) return text;
  }
  return null;
}

/** Safety Case span from group first/last occurrence when broadening beyond queue window. */
export function resolveFullCaseHistoryTimeBounds(
  group: CaseHistorySource | null | undefined,
): CaseHistoryTimeBounds {
  const fromIso = pickFirstInstant(group?.first_seen, group?.started_at);
  const toIso = pickFirstInstant(group?.last_seen, group?.last_seen_at) || new Date().toISOString();
  return { fromIso, toIso };
}

const LAST_24H_LABEL = 'Last 24h';

export function formatReviewWindowTimestamp(value?: string | null, timeZone?: string | null): string | null {
  const formatted = formatAfterSignedScanTimestamp(value, timeZone);
  if (!formatted || formatted === 'Not recorded') return null;
  return formatted;
}

export function resolveReviewWindowContext(args: ResolveReviewWindowContextArgs): ReviewWindowContext {
  const {
    timeRange,
    afterSignedScanReview,
    fullHistoryMode = false,
    caseHistoryBounds,
    signedScanScope,
  } = args;
  const presetLabel = String(timeRange.label || '').trim() || undefined;

  if (afterSignedScanReview?.active) {
    const signedScanAt = afterSignedScanReview.signedScanAt;
    const fromIso = afterSignedScanReview.scanScopeStart
      || afterSignedScanReview.newActivityWindowStart
      || signedScanAt
      || null;
    const toIso = afterSignedScanReview.scanScopeEnd || timeRange.toDate || null;
    return {
      kind: 'after_signed_scan',
      timelineMode: afterSignedScanReview.timelineMode,
      fromIso,
      toIso,
      signedScanAt,
      labels: { presetLabel },
    };
  }

  if (fullHistoryMode) {
    return {
      kind: 'full_case_history',
      fromIso: caseHistoryBounds?.fromIso ?? timeRange.fromDate ?? null,
      toIso: caseHistoryBounds?.toIso ?? timeRange.toDate ?? null,
      labels: { presetLabel: 'Full Safety Case history' },
    };
  }

  if (signedScanScope?.start || signedScanScope?.end) {
    return {
      kind: 'signed_scan_scope',
      fromIso: signedScanScope.start || null,
      toIso: signedScanScope.end || null,
      labels: { presetLabel },
    };
  }

  return {
    kind: 'selected_review_window',
    fromIso: timeRange.fromDate || null,
    toIso: timeRange.toDate || null,
    labels: { presetLabel },
  };
}

export function formatReviewWindowHeaderLabel(ctx: ReviewWindowContext, timeZone?: string | null): string {
  switch (ctx.kind) {
    case 'after_signed_scan':
      if (ctx.timelineMode === 'custom_new_activity') {
        const from = formatReviewWindowTimestamp(ctx.fromIso, timeZone);
        const to = formatReviewWindowTimestamp(ctx.toIso, timeZone);
        if (from && to) return `New activity window: ${from} → ${to}`;
        return 'New activity window: Selected new-activity window';
      }
      return 'New activity window: Since latest Signed Safety Scan';
    case 'full_case_history': {
      const from = formatReviewWindowTimestamp(ctx.fromIso, timeZone);
      const to = formatReviewWindowTimestamp(ctx.toIso, timeZone);
      if (from && to) return `Full Safety Case history: ${from} → ${to}`;
      return 'Full Safety Case history';
    }
    case 'signed_scan_scope': {
      const from = formatReviewWindowTimestamp(ctx.fromIso, timeZone);
      const to = formatReviewWindowTimestamp(ctx.toIso, timeZone);
      if (from && to) return `Signed Safety Scan scope: ${from} → ${to}`;
      return 'Signed Safety Scan scope';
    }
    default: {
      const from = formatReviewWindowTimestamp(ctx.fromIso, timeZone);
      const to = formatReviewWindowTimestamp(ctx.toIso, timeZone);
      if (from && to) return `Review window: ${from} → ${to}`;
      if (ctx.labels.presetLabel === LAST_24H_LABEL) {
        return 'Review window: Last 24h';
      }
      if (ctx.labels.presetLabel) {
        return `Review window: ${ctx.labels.presetLabel}`;
      }
      return `Review window: ${from || 'Not recorded'} → ${to || 'Not recorded'}`;
    }
  }
}

export function formatReviewWindowHeaderCaption(ctx: ReviewWindowContext): string {
  switch (ctx.kind) {
    case 'after_signed_scan':
      return 'New activity window';
    case 'full_case_history':
      return 'Safety Case history';
    case 'signed_scan_scope':
      return 'Signed Safety Scan scope';
    default:
      return 'Review window';
  }
}

export function formatReviewWindowHeaderValue(ctx: ReviewWindowContext, timeZone?: string | null): string {
  const full = formatReviewWindowHeaderLabel(ctx, timeZone);
  const colonIdx = full.indexOf(':');
  if (colonIdx >= 0) return full.slice(colonIdx + 1).trim();
  return full;
}

export function formatReviewWindowFromLabel(ctx: ReviewWindowContext, timeZone?: string | null): string {
  if (ctx.kind === 'after_signed_scan') {
    return formatAfterSignedScanTimestamp(ctx.fromIso, timeZone);
  }
  return formatReviewWindowTimestamp(ctx.fromIso, timeZone) || 'Not recorded';
}

export function formatReviewWindowToLabel(ctx: ReviewWindowContext, timeZone?: string | null): string {
  return formatReviewWindowTimestamp(ctx.toIso, timeZone) || 'Not recorded';
}

export function formatObservationCountLine(
  count: number,
  ctx: ReviewWindowContext,
  metric?: CustomerMetric | null,
): string {
  const n = Math.max(0, Number(count) || 0);
  const observations = formatObservationCount(n);
  switch (ctx.kind) {
    case 'after_signed_scan':
      if (!metricUsesSignedScanWindow(metric, ctx.signedScanAt)) {
        return `${observations} in current new-activity window`;
      }
      if (ctx.timelineMode === 'since_scan_custom_end') {
        return `${observations} from latest Signed Safety Scan to selected end`;
      }
      if (ctx.timelineMode === 'custom_new_activity') {
        return `${observations} in selected new-activity window`;
      }
      return `${observations} since latest Signed Safety Scan`;
    case 'full_case_history':
      return `${observations} in full Safety Case history`;
    case 'signed_scan_scope':
      return `${observations} in Signed Safety Scan scope`;
    default:
      return `${observations} in selected review window`;
  }
}

export function formatObservationBadge(
  count: number,
  ctx: ReviewWindowContext,
  metric?: CustomerMetric | null,
): string {
  const n = Math.max(0, Number(count) || 0);
  if (n === 0) return 'Representative evidence selected';
  if (n === 1 && ctx.kind === 'selected_review_window') {
    return 'Representative evidence selected';
  }
  return formatObservationCountLine(n, ctx, metric);
}

/** True only when the active window is explicitly the 24-hour preset. */
export function reviewWindowUsesLast24hPreset(ctx: ReviewWindowContext): boolean {
  return ctx.kind === 'selected_review_window' && ctx.labels.presetLabel === LAST_24H_LABEL;
}
